import FormContainer from '@components-member/TradingAccounts/AccountDetails/FormContainer';
import {
  EnumUTAInvestorPasswordFormField,
  EnumUTALeverageFormField,
  EnumUTAMasterPasswordFormField,
} from '@components-member/TradingAccounts/AccountDetails/enums';
import { CONSTANTS } from '@components-shared/constants';
import { validationService } from '@components-shared/forms/validators';
import { keyMap } from '@locales/lokalise/keyMap';
import { accountCardDepositClickEvent } from 'components/user/psp/deposit/analyticsEvents/depositAnalyticsEvents';
import { TRADING_ACCOUNT_TYPE } from 'global.enums';
import { IFormConfig } from '../interfaces';

export const MASTER_PASSWORD_FORM: IFormConfig = {
  title: keyMap.tradingAccountsMasterPasswordFormTitle,
  link: 'masterpassword',
  errorMessage: keyMap.tradingAccountsMasterPasswordFormErrorMessage,
  successMessage: keyMap.tradingAccountsMasterPasswordFormSuccessMessage,
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.REAL, CONSTANTS.TRADING_ACCOUNT_TYPE.DEMO],
  settingName: 'changeMasterPasswordAllowed',
  resetFields: true,
  fields: [
    {
      name: EnumUTAMasterPasswordFormField.password,
      title: keyMap.tradingAccountsMasterPasswordFormPasswordTitle,
      type: 'password',
      attributes: {
        autoComplete: 'off',
        usePasswordStrengthMeterContext: true,
      },
      required: true,
      limit: 64,
      hideLimitValue: true,
      validator: validationService.validatePassword(),
    },
    {
      name: EnumUTAMasterPasswordFormField.confirmPassword,
      title: keyMap.tradingAccountsMasterPasswordFormConfirmPasswordTitle,
      type: 'password',
      attributes: {
        autoComplete: 'off',
      },
      required: true,
      confirm: 'password',
    },
    {
      name: EnumUTAMasterPasswordFormField.pinCode,
      title: keyMap.tradingAccountsMasterPasswordFormPinCodeTitle,
      type: 'password',
      attributes: {
        autoComplete: 'off',
      },
      required: true,
      hintMessage: keyMap.pinCodePinCodeHint,
      validator: validationService.validatePinCodeLength(keyMap.pinCodePinCodeValidationMessage, 6),
    },
  ],
};

export const INVESTOR_PASSWORD_FORM: IFormConfig = {
  title: keyMap.tradingAccountsInvestorPasswordFormTitle,
  link: 'investorpassword',
  errorMessage: keyMap.tradingAccountsInvestorPasswordFormErrorMessage,
  successMessage: keyMap.tradingAccountsInvestorPasswordFormSuccessMessage,
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.REAL, CONSTANTS.TRADING_ACCOUNT_TYPE.DEMO],
  settingName: 'changeInvestorPasswordAllowed',
  resetFields: true,
  fields: [
    {
      name: EnumUTAInvestorPasswordFormField.password,
      title: keyMap.tradingAccountsInvestorPasswordFormPasswordTitle,
      type: 'password',
      attributes: {
        autoComplete: 'off',
        usePasswordStrengthMeterContext: true,
      },
      required: true,
      limit: 64,
      hideLimitValue: true,
      validator: validationService.validatePassword(),
    },
    {
      name: EnumUTAInvestorPasswordFormField.confirmPassword,
      title: keyMap.tradingAccountsInvestorPasswordFormConfirmPasswordTitle,
      type: 'password',
      attributes: {
        autoComplete: 'off',
      },
      required: true,
      validator: validationService.validatePassword(),
      confirm: 'password',
    },
    {
      name: EnumUTAInvestorPasswordFormField.pinCode,
      title: keyMap.tradingAccountsInvestorPasswordFormPinCodeTitle,
      type: 'password',
      attributes: {
        autoComplete: 'off',
      },
      required: true,
      hintMessage: keyMap.pinCodePinCodeHint,
      validator: validationService.validatePinCodeLength(keyMap.pinCodePinCodeValidationMessage, 6),
    },
  ],
};

export const LEVERAGE_FORM: Omit<IFormConfig, 'accountTypes'> & { accountTypes: TRADING_ACCOUNT_TYPE[] } = {
  title: keyMap.tradingAccountsLeverageFormTitle,
  link: 'leverage',
  errorMessage: keyMap.tradingAccountsLeverageFormErrorMessage,
  successMessage: keyMap.tradingAccountsLeverageFormSuccessMessage,
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.REAL, CONSTANTS.TRADING_ACCOUNT_TYPE.DEMO],
  settingName: 'changeLeverageAllowed',
  fields: [
    {
      name: EnumUTALeverageFormField.leverage,
      title: keyMap.commonLeverage,
      type: 'select',
      options: [],
      attributes: {
        autoComplete: 'off',
      },
      required: true,
      hintMessage: keyMap.tradingAccountsLeverageFormLeverageHint,
    },
  ],
};

export const TRANSFER_FORM = {
  title: keyMap.tradingAccountsTransferFormTitle,
  link: 'transfer',
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.REAL],
  settingName: 'withdrawalAllowed',
};

export const DEPOSIT_FORM = {
  title: keyMap.tradingAccountsDepositFormTitle,
  link: 'deposit',
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.REAL],
  settingName: 'depositAllowed',
  onClick: accountCardDepositClickEvent,
};

export const WITHDRAW_FORM = {
  title: keyMap.tradingAccountsWithdrawFormTitle,
  link: 'withdraw',
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.REAL],
  settingName: 'withdrawalAllowed',
};

export const REFILL_FORM: IFormConfig = {
  title: keyMap.tradingAccountsRefillFormRefillTitle,
  link: 'refill',
  apiParams: ['amount'],
  errorMessage: keyMap.tradingAccountsRefillFormRefillErrorMessage,
  successMessage: keyMap.tradingAccountsRefillFormRefillSuccessMessage,
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.DEMO],
  settingName: 'depositAllowed',
  fields: [
    {
      name: 'amount',
      title: keyMap.tradingAccountsRefillFormRefillAmountTitle,
      type: 'number',
      attributes: {
        autoComplete: 'off',
      },
      required: true,
    },
  ],
};

export const ACCOUNT_HISTORY = {
  title: keyMap.tradingAccountsAccountHistoryFormTitle,
  link: 'history',
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.REAL, CONSTANTS.TRADING_ACCOUNT_TYPE.DEMO],
  settingName: 'historyAllowed',
};

export const SOCIAL_TRADING_SETTINGS_INVESTOR = {
  title: keyMap.tradingAccountsSocialTradingSettings,
  link: 'follower',
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.REAL],
  settingName: 'socialTradingSettingsAllowed',
};

export const SOCIAL_TRADING_SETTINGS_MASTER = {
  title: keyMap.tradingAccountsSocialTradingSettings,
  link: 'master',
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.REAL],
  settingName: 'socialTradingSettingsAllowed',
};

export const MAM_INVESTOR_CANCEL_LPOA = {
  title: keyMap.tradingAccountsTerminateLPOA,
  link: 'terminateLPOA',
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.REAL],
  settingName: 'mamInvestorSettingsAllowed',
};

export const ACCOUNT_ARCHIVING_FORM = {
  title: keyMap.tradingAccountsArchvingFormTitle,
  link: 'archive',
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.REAL, CONSTANTS.TRADING_ACCOUNT_TYPE.DEMO],
  settingName: 'archivingAllowed',
};

export const PUSH_NOTIFICATIONS = {
  title: keyMap.pushNotifications,
  link: 'notifications',
  accountTypes: [CONSTANTS.TRADING_ACCOUNT_TYPE.REAL, CONSTANTS.TRADING_ACCOUNT_TYPE.DEMO],
  settingName: 'pushNotificationsAllowed',
};

export const MasterPasswordForm = FormContainer(MASTER_PASSWORD_FORM);
export const InvestorPasswordForm = FormContainer(INVESTOR_PASSWORD_FORM);
export const LeverageForm = FormContainer(LEVERAGE_FORM);
export const RefillForm = FormContainer(REFILL_FORM);
